import { Box, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";

const FineContainer = styled(Box)(({ theme }) => ({
  maxWidth: "1350px",
  margin: "0 auto",
  padding: theme.spacing(5, 6),
  borderRadius: "16px",
  backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
  border: `1px solid ${
    theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)"
  }`,
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)",
  },
}));

const FinePrintList = styled("ul")(({ theme }) => ({
  listStyle: "none",
  padding: 0,
  margin: 0,
  "& li": {
    position: "relative",
    paddingLeft: "1.2em",
    marginBottom: "10px",
    lineHeight: 1.6,
    color: theme.palette.mode === "dark" ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.7)",
    "&:last-child": {
      marginBottom: 0,
    },
    "&::before": {
      content: '"•"',
      position: "absolute",
      left: 0,
      color: theme.palette.mode === "dark" ? "#2ab06f" : "#238856",
      fontWeight: "bold",
    },
    "&:hover": {
      color: theme.palette.mode === "dark" ? "#fff" : "#000",
      transition: "color 0.3s ease",
    },
  },
}));

const Highlight = styled("span")(({ theme }) => ({
  color: theme.palette.mode === "dark" ? "#2ab06f" : "#238856",
  fontWeight: 500,
}));

const FinePrint = ({ darkMode, isBrandPage, landingData }) => {
  const hasLoyaltyPlan = !!landingData?.loyaltyPlan?.plans?.length;

  const finePoints = [
    {
      text: "Your rewards are paid out with Amazon.com Gift Cards after you reach 2000 earned points ($20). Rewards earned cannot be combined with any other offer. Can't be redeemed for cash or transferred for value. Rewards are earned based on terms and conditions set forth by each restaurant.",
      highlight: false,
    },
    {
      text: "Order size above refers to the subtotal (after discounts and excludes tips, taxes, & other fees).",
      highlight: false,
    },
    {
      text: isBrandPage
        ? "Total meal spend refers to the subtotal (after discounts and excludes tips, taxes, & other fees) spent in a 12 month period from your anniversary date across all restaurants under the brand."
        : "Total meal spend refers to the subtotal (after discounts and excludes tips, taxes, & other fees) spent in a 12 month period from your anniversary date at a specific restaurant.",
      highlight: false,
    },
    ...(isBrandPage && !hasLoyaltyPlan
      ? []
      : [
          {
            text: "You earn 1 point for every $1 spent on food, if your order meets the minimum requirements specified in the table above.",
            highlight: false,
          },
        ]),
    ...(isBrandPage && !hasLoyaltyPlan
      ? []
      : [
          {
            text: `Earn higher points based on the "Order Size" and the "Level". Details are in the table above.`,
            highlight: true,
          },
        ]),
    ...(isBrandPage && !hasLoyaltyPlan
      ? []
      : [
          {
            text: "100 points is equivalent to $1 in earned cashback rewards. So a 9X cashback on $1000 (sub-total) order will result in 9000 points = $90 in Amazon.com Gift Cards.",
            highlight: true,
          },
        ]),
    {
      text: isBrandPage
        ? "LEVEL resets to LEVEL 1 after 12 months from your anniversary date across all restaurants under the brand."
        : "LEVEL for a particular restaurant resets to LEVEL 1 after 12 months from that restaurant's anniversary date.",
      highlight: false,
    },
    {
      text: "Rewards are valid only for catering menu orders placed through the catering website.",
      highlight: true,
    },
    {
      text: `Amazon.com is not a sponsor of this promotion. Except as required by law, Amazon.com Gift Cards ("GCs") cannot be transferred for value or redeemed for cash. GCs may be used only for purchases of eligible goods at Amazon.com or certain of its affiliated websites. For complete terms and conditions, see www.amazon.com/gc-legal. GCs are issued by ACI Gift Cards, Inc., a Washington corporation. ©,®,™ Amazon.com Inc. and/or its affiliates, 2013. No expiration date or service fees.`,
      isAmazon: true,
    },
  ];

  return (
    <FineContainer>
      <Typography
        variant="overline"
        sx={{
          display: "block",
          mb: 3,
          color: darkMode ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)",
          letterSpacing: "0.1em",
          fontSize: "0.75rem",
          fontWeight: 600,
        }}
      >
        Fine Print
      </Typography>

      <FinePrintList>
        {finePoints.map((point, index) => (
          <li
            key={index}
            style={
              point.isAmazon
                ? { fontSize: "0.75rem", opacity: 0.8 }
                : { fontSize: "13px", opacity: 0.8 }
            }
          >
            {point.highlight ? <Highlight>{point.text}</Highlight> : point.text}
          </li>
        ))}
      </FinePrintList>
    </FineContainer>
  );
};

export default FinePrint;

FinePrint.propTypes = {
  darkMode: PropTypes.any,
  isBrandPage: PropTypes.bool,
  landingData: PropTypes.shape({
    loyaltyPlan: PropTypes.object,
  }),
};
